import { useCallback, useMemo } from 'react';
import { type ContextualMenuProps } from '@adc/parallax-component-library';
import { useLocation, useNavigate } from '@tanstack/react-router';

import { AppMenuItemIcon } from '~/components/navigation/AppMenuItemIcon';
import { USER_ROLES } from '~/features/auth/roles';
import { useAuthState } from '~/features/auth/useAuthState';
import {
  getAppMenuItemsByRole,
  ROLE_NAVIGATION_MAP,
} from '~/features/navigation/utils';
import { toCapitalAcronym } from '~/utils/toCapitalAcronym';

export const useNavigationItems = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isAuthenticated, userInfo } = useAuthState();

  const userRole = userInfo?.role ?? undefined;

  const appMenuItems = useMemo(() => {
    if (!isAuthenticated || !userRole) {
      return [];
    }

    return getAppMenuItemsByRole(userRole).map(
      (i) =>
        ({
          ...i,
          iconStart: <AppMenuItemIcon />,
        }) satisfies ContextualMenuProps['items'][number],
    );
  }, [userRole, isAuthenticated]);

  const appMenuItemSelected = useMemo(
    () =>
      appMenuItems.findIndex(({ id }) => {
        const navItems = userRole ? ROLE_NAVIGATION_MAP[userRole][id] : [];

        // Note: we are using `.startsWith` instead of equality check for cases like `/department` and `/department/{id}`
        return navItems?.some(({ path }) => pathname.startsWith(path));
      }),
    [appMenuItems, userRole, pathname],
  );

  const currentlySelectedAppMenuItemId = appMenuItems[appMenuItemSelected]?.id;

  const navigationItems = useMemo(() => {
    if (!isAuthenticated || !userRole) {
      return [];
    }

    const navigationItemsByRole = currentlySelectedAppMenuItemId
      ? (ROLE_NAVIGATION_MAP[userRole][currentlySelectedAppMenuItemId] ?? [])
      : [];

    if (userInfo?.departmentName && userInfo.role === USER_ROLES.HcpUser) {
      const patientsDashboard = navigationItemsByRole.find(
        (item) => item.id === 'patients',
      );

      if (!patientsDashboard) {
        return navigationItemsByRole;
      }

      return [
        {
          ...patientsDashboard,
          label: toCapitalAcronym(userInfo.departmentName),
        },
        ...navigationItemsByRole.filter((item) => item.id !== 'patients'),
      ];
    }

    return navigationItemsByRole;
  }, [isAuthenticated, userRole, currentlySelectedAppMenuItemId, userInfo]);

  const handleSelectAppMenuItem = useCallback(
    (itemIndex: number) => {
      const domain = appMenuItems[itemIndex]?.id;
      const newPath =
        userRole && domain
          ? ROLE_NAVIGATION_MAP[userRole][domain]?.[0]?.path
          : undefined;

      if (newPath) {
        void navigate({ to: newPath });
      }
    },
    [navigate, userRole, appMenuItems],
  );

  return {
    navigationItems,
    appMenuItems,
    handleSelectAppMenuItem,
    appMenuItemSelected,
  };
};
